<template>
    <div>
        <header class="py-3">
            <b-container>
                <b-row class="align-items-center">
                    <b-col cols="12" md="8" class="pt-2 pt-md-3 order-2 order-md-1">
                        <div class="header-content">
                            <h4 class="mb-0"><i class="fas fa-file-signature mr-2 d-none d-sm-inline"></i>{{agreementConfig.title}}</h4>
                        </div>
                    </b-col>
                    <b-col cols="12" md="4" class="text-center text-md-right order-1 order-md-2">
                        <router-link to="/"><img src="@/assets/thepaymentoption-logo-white.svg" class="logo"/></router-link>
                    </b-col>
                </b-row>
            </b-container>
        </header>

        <TermsDialog v-model="showTermsDialog" :plan="plan" :form-data="formData" :config="agreementConfig" />

        <b-container class="mt-5 pb-5 mb-5" v-if="plan.id">
            <!-- Completion Message -->
            <div v-if="submissionComplete" class="completion-message">
                <b-row>
                    <b-col cols="12">
                        <b-card class="text-center py-5">
                            <div class="mb-4">
                                <i class="fas fa-check-circle" style="font-size: 80px; color: #28a745;"></i>
                            </div>
                            <h2 class="mb-4" style="color: #464577;">{{agreementConfig.completionTitle}}</h2>
                            <p class="lead mb-2">{{agreementConfig.completionMessage}}</p>
                            <p class="mb-4">{{agreementConfig.completionSubMessage}}</p>
                            <b-button
                                variant="primary"
                                class="mb-4"
                                :href="`${apiUrlBase}${apiPath}/download-signed`"
                                target="_blank"
                            >
                                <i class="fas fa-download mr-2"></i>Download Signed Agreement
                            </b-button>
                        </b-card>
                    </b-col>
                </b-row>

                <b-row class="mt-4">
                    <b-col cols="12">
                        <b-card>
                            <h5 style="color: #464577;">What Happens Next</h5>
                            <div class="next-steps mt-3">
                                <div class="step-item">
                                    <i class="fas fa-calendar-check text-primary fa-fw"></i>
                                    <div>
                                        <strong>Automatic Payments</strong>
                                        <p class="text-muted mb-0">Your payments will be automatically processed on the 1st of each month beginning on your first payment date.</p>
                                    </div>
                                </div>
                                <div class="step-item">
                                    <i class="fas fa-credit-card text-warning fa-fw"></i>
                                    <div>
                                        <strong>Payment Method Required</strong>
                                        <p class="text-muted mb-0">If you have not yet added a payment method, you must do so before your first payment date. Log in to your account at <a href="https://app.thepaymentoption.com">app.thepaymentoption.com</a> to add your payment information.</p>
                                    </div>
                                </div>
                                <div class="step-item">
                                    <i class="fas fa-user-circle text-secondary fa-fw"></i>
                                    <div>
                                        <strong>Your Account</strong>
                                        <p class="text-muted mb-0">You can access your account anytime at <a href="https://app.thepaymentoption.com">app.thepaymentoption.com</a>. Log in with your email address or phone number (SMS).</p>
                                    </div>
                                </div>
                                <div class="step-item">
                                    <i class="fas fa-question-circle text-info fa-fw"></i>
                                    <div>
                                        <strong>Need Help?</strong>
                                        <p class="text-muted mb-0">For questions about your agreement or payments, contact <strong>The Payment Option</strong>, your lender, at <a href="mailto:help@thepaymentoption.com">help@thepaymentoption.com</a> or text (844) 540-2400. For questions about your purchase with <strong>{{agreementConfig.merchant.name}}</strong>, contact them at <a :href="'mailto:' + agreementConfig.merchant.email">{{agreementConfig.merchant.email}}</a> or {{agreementConfig.merchant.phone}}.</p>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </b-col>
                </b-row>
            </div>

            <!-- Step Components -->
            <div v-else>
                <AgreementDetails
                    v-if="currentStep === 1"
                    :plan="plan"
                    :terms-acknowledged="termsAcknowledged"
                    :current-step="currentStep"
                    :config="agreementConfig"
                    @show-terms="showTermsDialog = true"
                    @audit-event="onAuditEvent"
                />

                <InformationVerification
                    v-if="currentStep === 2"
                    :form-data="formData"
                    :api-path="apiPath"
                    :current-step="currentStep"
                    :verified-data-from-db="verifiedDataFromDb"
                    :config="agreementConfig"
                    @validation-changed="onStep2ValidationChanged"
                    @verification-data-changed="onVerificationDataChanged"
                    @show-terms="showTermsDialog = true"
                    @audit-event="onAuditEvent"
                />

                <IdVerification
                    v-if="idVerificationEnabled && currentStep === stepIdVerification"
                    :api-path="apiPath"
                    :current-step="currentStep"
                    :config="agreementConfig"
                    :verified-sms="(verifiedDataFromDb && verifiedDataFromDb.verified_sms) || formData.phone"
                    :initial-upload-id="plan.id_upload_id"
                    @validation-changed="onIdValidationChanged"
                    @show-terms="showTermsDialog = true"
                    @audit-event="onAuditEvent"
                />

                <PaymentMethod
                    v-if="currentStep === stepPayment"
                    :plan="plan"
                    :api-path="apiPath"
                    :selected-payment-method.sync="selectedPaymentMethod"
                    :payment-method-acknowledged.sync="paymentMethodAcknowledged"
                    :current-step="currentStep"
                    :config="agreementConfig"
                    :existing-payment-methods="existingPaymentMethods"
                    @ach-data-changed="onAchDataChanged"
                    @payment-method-saved="onPaymentMethodSaved"
                    @validation-changed="onStep4ValidationChanged"
                    @show-terms="showTermsDialog = true"
                    @audit-event="onAuditEvent"
                />

                <ReviewAndConsent
                    v-if="currentStep === stepReview"
                    ref="reviewAndConsent"
                    :plan="plan"
                    :form-data="formData"
                    :current-step="currentStep"
                    :config="agreementConfig"
                    @validation-changed="onStep4ConsentChanged"
                    @consent-given="onConsentGiven"
                    @show-terms="showTermsDialog = true"
                    @audit-event="onAuditEvent"
                />

                <SignDocument
                    v-if="currentStep === stepSign"
                    ref="signDocument"
                    :plan="plan"
                    :form-data="formData"
                    :current-step="currentStep"
                    :api-path="apiPath"
                    :consent-data="consentData"
                    :config="agreementConfig"
                    @validation-changed="onFinalValidationChanged"
                    @document-presented="onDocumentPresented"
                    @signature-started="onSignatureStarted"
                    @signature-cleared="onSignatureCleared"
                    @show-terms="showTermsDialog = true"
                    @audit-event="onAuditEvent"
                />
            </div>

            <!-- Navigation Buttons -->
            <div class="wizard-navigation" v-if="!submissionComplete">
                <b-container>
                    <b-row>
                        <b-col class="text-center">
                            <b-button
                                v-if="currentStep > 1"
                                variant="outline-primary"
                                class="mr-3"
                                @click="previousStep">
                                <i class="fas fa-arrow-left mr-2"></i>Previous
                            </b-button>

                            <b-button
                                v-if="currentStep < totalSteps"
                                variant="primary"
                                :disabled="!canProceedFromCurrentStep"
                                @click="nextStep">
                                Next<i class="fas fa-arrow-right ml-2"></i>
                            </b-button>

                            <b-button
                                v-if="currentStep === stepSign"
                                variant="success"
                                :disabled="!canProceedToSigning"
                                @click="proceedToSigning"
                                class="sign-button">
                                <i v-if="isLoading" class="fas fa-spinner fa-spin mr-2"></i>
                                <i v-else class="fas fa-file-signature mr-2"></i>
                                {{buttonText}}
                            </b-button>
                        </b-col>
                    </b-row>
                </b-container>
            </div>
        </b-container>

        <b-container class="mt-5 pb-5 mb-5" v-else-if="alreadyOnboarded">
            <b-row>
                <b-col cols="12">
                    <b-card class="text-center py-5">
                        <div class="mb-5">
                            <i class="fal fa-check-circle" style="font-size: 80px; color: #28a745;"></i>
                        </div>
                        <h2 class="mb-4" style="color: #464577;">Onboarding Complete</h2>
                        <p class="lead mb-4">{{ onboardingMessage }}</p>
                        <p class="text-muted">If you have any questions, please contact us at <a href="mailto:help@thepaymentoption.com">help@thepaymentoption.com</a>.</p>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <b-container class="mt-5 pb-5 mb-5" v-else-if="expired">
            <div class="mb-5">Unfortunately, this agreement signing session has expired or is invalid.</div>
            <b-button variant="success" :to="{name: 'home'}"><i class="fas fa-home mr-2"></i>Return to Home</b-button>
        </b-container>

        <b-container class="mt-5 pb-5 mb-5" v-else>
            <div class="mb-5"><i class="fal fa-sync fa-spin mr-3"></i>Loading agreement details...</div>
        </b-container>

        <footer class="pt-3 pb-2">
            <b-container>
                <Copyright class="mt-3 mb-3"/>
            </b-container>
        </footer>
    </div>
</template>

<script>
    import Copyright from "../components/Copyright";
    import {apiUrl} from '@/constants/config';
    import axios from 'axios';

    // Import wizard step components (isolated copies for new sign-agreement flow)
    import AgreementDetails from './sign-agreement/AgreementDetails.vue';
    import InformationVerification from './sign-agreement/InformationVerification.vue';
    import IdVerification from './sign-agreement/IdVerification.vue';
    import PaymentMethod from './sign-agreement/PaymentMethod.vue';
    import ReviewAndConsent from './sign-agreement/ReviewAndConsent.vue';
    import SignDocument from './sign-agreement/SignDocument.vue';
    import TermsDialog from '@/components/sign-agreement/TermsDialog.vue';

    export default {
        components: {
            Copyright,
            AgreementDetails,
            InformationVerification,
            IdVerification,
            PaymentMethod,
            ReviewAndConsent,
            SignDocument,
            TermsDialog
        },
        props: ['hash'],
        data: function() {
            return {
                expired: false,
                alreadyOnboarded: false,
                onboardingMessage: '',
                isLoading: false,
                currentStep: 1,
                plan: {},
                agreementConfig: {},
                existingPaymentMethods: [],
                showTermsDialog: false,
                formData: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    address: '',
                    address_2: '',
                    city: '',
                    state: '',
                    zip: '',
                    phone: '',
                    phone_2: '',
                    email_2: ''
                },
                termsAcknowledged: [],
                // Payment method step data
                selectedPaymentMethod: 'ach',
                paymentMethodAcknowledged: false,
                achData: {},
                // Final acknowledgments data
                finalAcknowledgments: {
                    reviewedInformation: false,
                    understandTerms: false,
                    readyToSign: false
                },
                finalValidationPassed: false,
                step2ValidationPassed: false,
                idUploadValidationPassed: false,
                step3ValidationPassed: false,
                step4ConsentPassed: false,
                consentData: {},
                submissionComplete: false,
                verificationData: {},
                verifiedDataFromDb: {},
                // E-signature session tracking
                sessionId: null,
                signatureEvents: []
            };
        },
        computed: {
            apiUrlBase() {
                return apiUrl;
            },
            apiPath() {
                return `api/account/agreement-sign/${this.hash}`;
            },
            idVerificationEnabled() {
                return !!(this.agreementConfig
                    && this.agreementConfig.idVerification
                    && this.agreementConfig.idVerification.required);
            },
            totalSteps() {
                return this.idVerificationEnabled ? 6 : 5;
            },
            stepIdVerification() {
                return this.idVerificationEnabled ? 3 : null;
            },
            stepPayment() {
                return this.idVerificationEnabled ? 4 : 3;
            },
            stepReview() {
                return this.idVerificationEnabled ? 5 : 4;
            },
            stepSign() {
                return this.idVerificationEnabled ? 6 : 5;
            },
            currentStepTitle() {
                if (this.currentStep === 1) return 'Agreement Details';
                if (this.currentStep === 2) return 'Verify Information';
                if (this.currentStep === this.stepIdVerification) return 'Verify Identity';
                if (this.currentStep === this.stepPayment) return 'Payment Method';
                if (this.currentStep === this.stepReview) return 'Review & Consent';
                if (this.currentStep === this.stepSign) return 'Sign Document';
                return 'Agreement Details';
            },
            allTermsAcknowledged() {
                return this.termsAcknowledged.every(term => term);
            },
            allRequiredFieldsFilled() {
                return this.formData.first_name &&
                       this.formData.last_name &&
                       this.formData.email &&
                       this.formData.email_2 &&
                       this.formData.address &&
                       this.formData.city &&
                       this.formData.state &&
                       this.formData.zip &&
                       this.formData.phone;
            },
            // Verification completion is now handled by individual step components
            canProceedToSigning() {
                return this.finalValidationPassed && !this.isLoading;
            },
            buttonText() {
                if (this.isLoading) return 'Signing Agreement... please wait...';
                return `Sign ${this.agreementConfig.shortTitle || 'Agreement'}`;
            },
            canProceedFromCurrentStep() {
                if (this.currentStep === 1) return this.allTermsAcknowledged;
                if (this.currentStep === 2) return this.step2ValidationPassed;
                if (this.currentStep === this.stepIdVerification) return this.idUploadValidationPassed;
                if (this.currentStep === this.stepPayment) return this.step3ValidationPassed;
                if (this.currentStep === this.stepReview) return this.step4ConsentPassed;
                if (this.currentStep === this.stepSign) return this.finalValidationPassed;
                return false;
            }
        },
        methods: {
            // Wizard Navigation Methods
            nextStep() {
                if (this.canProceedFromCurrentStep && this.currentStep < this.totalSteps) {
                    this.currentStep++;
                    this.scrollToTop();
                }
            },

            previousStep() {
                if (this.currentStep > 1) {
                    this.currentStep--;
                    this.scrollToTop();
                }
            },

            scrollToTop() {
                // Scroll to top of page smoothly when changing steps
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            },

            proceedToSigning() {
                if (this.canProceedToSigning) {
                    this.submitAgreementSigning();
                }
            },

            // Event Handlers for Step Components
            onStep2ValidationChanged(isValid) {
                this.step2ValidationPassed = isValid;
            },

            onIdValidationChanged(isValid) {
                this.idUploadValidationPassed = isValid;
            },

            onStep4ConsentChanged(isValid) {
                this.step4ConsentPassed = isValid;
            },

            onVerificationDataChanged(data) {
                this.verificationData = data;
            },

            onStep4ValidationChanged(isValid) {
                this.step3ValidationPassed = isValid;
            },

            onFinalValidationChanged(isValid) {
                this.finalValidationPassed = isValid;
            },

            onAchDataChanged(achData) {
                this.achData = achData;
            },

            onPaymentMethodSaved(data) {
                if (data.paymentMethodId) {
                    // Existing payment method linked
                    this.plan.payment_method_id = data.paymentMethodId;
                } else {
                    // New ACH was saved directly on the plan
                    this.plan.ach_provided = 1;
                }
                if (data.nickname) {
                    this.plan.ach_nickname = data.nickname;
                }
                if (data.lastFour) {
                    this.plan.ach_last_four = data.lastFour;
                }
            },

            // E-signature event handlers
            onDocumentPresented(data) {
                this.recordSignatureEvent('document_presented', data);
            },

            onConsentGiven(data) {
                // Save consent data for passing to SignDocument component
                this.consentData = {
                    consentGiven: true,
                    consentTimestamp: data.timestamp
                };
                this.recordSignatureEvent('consent_given', data);
            },

            onSignatureStarted(data) {
                this.recordSignatureEvent('signature_started', data);
            },

            onSignatureCleared(data) {
                this.recordSignatureEvent('signature_cleared', data);
            },

            recordSignatureEvent(eventType, data) {
                this.signatureEvents.push({
                    eventType,
                    ...data,
                    userAgent: navigator.userAgent
                });

                // Send to server for real-time audit logging
                this.sendAuditEvent(eventType, data);
            },

            // Unified audit event handler for all step components
            onAuditEvent(event) {
                const { eventType, data } = event;

                // Add to local event log
                this.signatureEvents.push({
                    eventType,
                    ...data,
                    timestamp: new Date().toISOString(),
                    userAgent: navigator.userAgent
                });

                // Send to server for real-time audit logging
                this.sendAuditEvent(eventType, data);
            },

            // Send audit event to server
            sendAuditEvent(eventType, data) {
                axios.post(`${apiUrl}${this.apiPath}/record-event`, {
                    eventType,
                    eventData: data,
                    timestamp: new Date().toISOString(),
                    sessionId: this.sessionId,
                    userAgent: navigator.userAgent
                }).catch(err => {
                    console.warn('Failed to record audit event:', err);
                });
            },

            // Data Loading
            async loadAgreementData() {
                try {
                    const response = await axios.get(`${apiUrl}${this.apiPath}`);
                    this.plan = response.data.plan;
                    this.agreementConfig = response.data.agreementConfig || {};

                    // Initialize terms checkboxes based on config
                    const numTerms = (this.agreementConfig.termsCheckboxes || []).length;
                    this.termsAcknowledged = new Array(numTerms).fill(false);

                    // Store existing payment methods
                    this.existingPaymentMethods = response.data.existingPaymentMethods || [];

                    // Pre-populate form with user data
                    this.formData = {
                        ...response.data.user,
                        email: response.data.user.email // Keep email readonly
                    };

                    // Load verified data from previous onboarding attempts
                    this.verifiedDataFromDb = {
                        verified_email: response.data.plan.verified_email || null,
                        verified_email_alt: response.data.plan.verified_email_alt || null,
                        verified_sms: response.data.plan.verified_sms || null,
                        verified_mailing_address: response.data.plan.verified_mailing_address || null,
                        postgrid_verified: response.data.plan.postgrid_verified || false,
                        postgrid_address_bypass: response.data.plan.postgrid_address_bypass || false
                    };
                } catch (error) {
                    console.error('Error loading agreement data:', error);

                    // Check if onboarding was already completed
                    if (error.response && error.response.status === 403 && error.response.data.error === 'Onboarding already completed') {
                        this.alreadyOnboarded = true;
                        this.onboardingMessage = error.response.data.message;
                    } else {
                        this.expired = true;
                    }
                }
            },

            /*
            ==================================================================
            REMOVED: All address/email/SMS verification methods moved to
            InformationVerification.vue component to avoid code duplication
            and expensive API calls on page load.
            ==================================================================
            */

            // REMOVED: ~700 lines of verification methods that belonged in InformationVerification.vue
            // Including: validateAddress, formatManualAddress, PostGrid API calls,
            // email verification, SMS verification, autocomplete, etc.

            async submitAgreementSigning() {
                if (!this.canProceedToSigning) return;

                // Get signature data from SignDocument component
                const signatureData = this.$refs.signDocument?.getSignatureData();
                if (!signatureData) {
                    this.$bvToast.toast(
                        'Please provide your signature before submitting.',
                        {
                            title: 'Signature Required',
                            variant: 'warning',
                            autoHideDelay: 5000
                        }
                    );
                    return;
                }

                this.isLoading = true;

                try {
                    const response = await axios.post(`${apiUrl}${this.apiPath}/submit-signature`, {
                        formData: this.formData,
                        termsAcknowledged: this.termsAcknowledged,
                        selectedPaymentMethod: this.selectedPaymentMethod,
                        paymentMethodAcknowledged: this.paymentMethodAcknowledged,
                        achData: this.achData,
                        finalAcknowledgments: this.finalAcknowledgments,
                        verificationData: this.verificationData,
                        // E-signature specific data
                        signatureData: {
                            signatureImageBase64: signatureData.image,
                            strokeDataJson: JSON.stringify(signatureData.strokeData),
                            signatureType: signatureData.type,
                            typedValue: signatureData.typedValue,
                            consentTimestamp: signatureData.consentTimestamp,
                            signatureStartTimestamp: signatureData.signatureStartTimestamp,
                            signatureCompletedTimestamp: signatureData.signatureCompletedTimestamp
                        },
                        sessionId: this.sessionId,
                        signatureEvents: this.signatureEvents
                    });

                    // Show completion message
                    this.isLoading = false;
                    this.submissionComplete = true;
                    this.scrollToTop();

                } catch (error) {
                    console.error('Error submitting agreement:', error);
                    this.isLoading = false;

                    const code = error.response?.data?.error;
                    const friendlyErrors = {
                        id_required: 'Please upload a photo of your government-issued ID before signing.',
                        id_not_valid: 'We couldn\'t verify the photo ID you uploaded. Please return to the Verify Identity step and upload a new photo.'
                    };

                    // Show error message
                    this.$bvToast.toast(
                        friendlyErrors[code] || 'An error occurred. Please try again or contact support.',
                        {
                            title: 'Error',
                            variant: 'danger',
                            autoHideDelay: 5000
                        }
                    );
                }
            }
        },
        mounted: function() {
            // Generate a unique session ID for audit tracking
            this.sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            this.loadAgreementData();
        }
    };
</script>

<style lang="scss" scoped>
    @import '~@/assets/scss/_variables.scss';

    .wizard-step {
        min-height: 400px;
    }

    .wizard-navigation {
        background: #f8f9fa;
        border-top: 1px solid #dee2e6;
        padding: 20px 0;
        margin-top: 40px;

        .btn {
            min-width: 120px;
        }
    }

    header {
        .logo {
            max-width: 140px;
        }

        background: #464577;
        color: white;

        h4 {
            font-size: 1.3rem;
        }

        .header-step {
            color: rgba(255, 255, 255, 0.9);
            font-size: 1rem;
            margin-top: 2px;
            margin-left: 38px; // Align under text, accounting for icon + margin
            font-weight: 400;
        }

        @media (max-width: 768px) {
            .logo {
                max-width: 100px;
                margin-bottom: 0.5rem;
            }

            h4 {
                font-size: 1.1rem;
                text-align: center;
            }

            .header-step {
                font-size: 0.9rem;
                margin-left: 0;
                text-align: center;
            }

            .header-content {
                text-align: center;
                padding-top: 0;
            }
        }
    }

    footer {
        border-top: 1px solid $gray-300;
        padding: 2rem 0 0;
        margin-top: 2rem;

        .copyright {
            color: $gray-500 !important;
        }
    }

    .sign-button {
        min-width: 200px;
        padding: 0.75rem 2rem;
        font-size: 1.1rem;
        font-weight: 600;

        @media (max-width: 768px) {
            width: 100%;
            margin-bottom: 1rem;
        }
    }

    .next-steps {
        .step-item {
            display: flex;
            align-items: flex-start;
            gap: 15px;
            padding: 15px 0;
            border-bottom: 1px solid #f0f0f0;

            &:last-child {
                border-bottom: none;
                padding-bottom: 0;
            }

            i {
                font-size: 1.2em;
                margin-top: 2px;
            }

            div {
                flex: 1;
            }
        }
    }
</style>